import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, getAlarmas, textoListaIncompleta } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AlarmRow from "@/components/consola/AlarmRow";
import AlarmDetail from "@/components/consola/AlarmDetail";
import CloseAlarmModal from "@/components/consola/CloseAlarmModal";
import TopBar from "@/components/consola/TopBar";
import ContextPanel from "@/components/consola/ContextPanel";
import { ordenarCola, contarTomadas } from "@/lib/consolaLib";
import { tipoMeta, prioridadMeta, elapsed } from "@/lib/alarmMeta";
import { BellRing, ArrowLeft, Wrench, ShieldOff } from "lucide-react";
import * as alarmSound from "@/lib/alarmSound";

const NOCHE_KEY = "galarya_consola_noche";
const CTX_KEY = "galarya_ctx_abierto";
const useNow = () => { const [n, s] = useState(Date.now()); useEffect(() => { const t = setInterval(() => s(Date.now()), 1000); return () => clearInterval(t); }, []); return n; };

function MiniCard({ a, nowMs }) {
  const pm = prioridadMeta(a.prioridad);
  const tec = a.es_tecnica;
  return (
    <div data-testid={tec ? "resumen-card-tecnica" : "resumen-card-persona"}
      className={`rounded-xl border border-l-4 p-3 ${tec
        ? "border-[#9B2C2C]/40 border-l-[#9B2C2C] bg-[#9B2C2C]/5 dark:bg-[#9B2C2C]/15"
        : "border-slate-200 dark:border-slate-700 border-l-slate-300 dark:border-l-slate-600"}`}>
      <div className="flex items-center gap-1.5">
        {tec && <Wrench className="h-3.5 w-3.5 shrink-0 text-[#9B2C2C] dark:text-[#F2B8B8]" />}
        <p className={`text-sm font-bold uppercase tracking-wide ${tec ? "text-[#9B2C2C] dark:text-[#F2B8B8]" : "text-[#012472] dark:text-slate-100"}`}>
          {tipoMeta(a.tipo).label}
        </p>
      </div>
      {!tec && a.persona_nombre && <p className="mt-0.5 text-sm font-medium text-slate-700 dark:text-slate-200">{a.persona_nombre}</p>}
      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
        {a.sitio_nombre}{a.zona_nombre ? ` · ${a.zona_nombre}` : ""} · <span className={pm.text}>{pm.label}</span> · {elapsed(a.hora_recepcion_servidor, nowMs)}
      </p>
    </div>
  );
}

function ResumenOperativo({ alarmas, mias, counts, nowMs, tecnicasAbiertas }) {
  const personaAbiertas = alarmas.filter((a) => !a.es_tecnica && a.estado !== "cerrada");
  const enRiesgo = alarmas.filter((a) => a.estado === "recibida" && !a.es_tecnica && a.prioridad === "alta");
  const topPersona = ordenarCola(alarmas.filter((a) => a.estado === "recibida" && !a.es_tecnica), nowMs).slice(0, 4);
  const topTecnicas = ordenarCola(alarmas.filter((a) => a.estado === "recibida" && a.es_tecnica), nowMs).slice(0, 4);
  const sinPersona = personaAbiertas.length === 0;
  return (
    <div data-testid="resumen-operativo" className="p-6 space-y-5 max-w-3xl mx-auto w-full">
      {mias.length > 0 && (
        <div data-testid="resumen-mias" className="rounded-xl border border-[#01CBE3]/40 bg-[#01CBE3]/5 p-4">
          <p className="text-xs font-semibold uppercase text-[#012472] dark:text-[#80B3FF]">Tus casos en atención</p>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {mias.map((a) => <MiniCard key={a.id} a={a} nowMs={nowMs} />)}
          </div>
        </div>
      )}
      {sinPersona ? (
        <div data-testid="estado-vacio" className="text-center py-10">
          <BellRing className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500 dark:text-slate-300 font-medium">
            {tecnicasAbiertas > 0 ? "Sin alarmas de persona pendientes" : "Sin alarmas pendientes"}
          </p>
          {tecnicasAbiertas > 0 && (
            <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-[#9B2C2C] dark:text-[#F2B8B8]">
              <Wrench className="h-4 w-4" /> {tecnicasAbiertas} incidencia(s) técnica(s) abierta(s)
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <Kpi n={counts.altaSinTomar} label="ALTA sin tomar" tone="text-red-600" />
            <Kpi n={counts.mediaSinTomar} label="MEDIA sin tomar" tone="text-amber-600" />
            <Kpi n={counts.enAtencion} label="En atención" tone="text-slate-600 dark:text-slate-300" />
            <Kpi n={counts.coberturaCritica} label="Cobertura crítica" tone="text-[#9B2C2C]" />
          </div>
          {enRiesgo.length > 0 && (
            <div data-testid="resumen-sla-riesgo" className="rounded-xl border border-red-300 bg-red-50 dark:bg-red-900/20 p-3">
              <p className="text-sm font-semibold text-red-700 dark:text-red-300">{enRiesgo.length} ALTA sin tomar — atiende cuanto antes</p>
            </div>
          )}
          {topPersona.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {topPersona.map((a) => <MiniCard key={a.id} a={a} nowMs={nowMs} />)}
            </div>
          )}
        </>
      )}
      {topTecnicas.length > 0 && (
        <div data-testid="resumen-tecnicas" className="space-y-2">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase text-slate-400">
            <Wrench className="h-3.5 w-3.5" /> Incidencias técnicas sin atender ({topTecnicas.length})
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {topTecnicas.map((a) => <MiniCard key={a.id} a={a} nowMs={nowMs} />)}
          </div>
        </div>
      )}
    </div>
  );
}
const Kpi = ({ n, label, tone }) => (<div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"><p className={`text-2xl font-bold tabular-nums ${tone}`}>{n}</p><p className="text-xs text-slate-500">{label}</p></div>);

export default function Consola() {
  const nowMs = useNow();
  const { user } = useAuth();
  const uid = user?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [alarmas, setAlarmas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [noche, setNoche] = useState(() => localStorage.getItem(NOCHE_KEY) === "1");
  const [loadError, setLoadError] = useState(false);
  const [listaParcial, setListaParcial] = useState("");   // "" = la cola se pudo recorrer entera
  const ticketRef = useRef(0);
  const [audioOn, setAudioOn] = useState(false);
  const [cfg, setCfg] = useState({ max_alta_tomadas: 1, max_media_tomadas: 3, sesion_heartbeat_seg: 15 });
  const [lastOk, setLastOk] = useState(0);
  const [colaOpen, setColaOpen] = useState(false);
  const [ctxOpen, setCtxOpen] = useState(false);
  const [ctxDesktop, setCtxDesktop] = useState(() => {
    const saved = sessionStorage.getItem(CTX_KEY);
    if (saved !== null) return saved === "1";
    return typeof window !== "undefined" && window.matchMedia("(min-width:1600px)").matches;
  });
  const [notas, setNotas] = useState({});
  const selRef = useRef(null); selRef.current = selectedId;
  const seenRef = useRef(new Set());

  useEffect(() => { api.get("/config-operacion").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  useEffect(() => { alarmSound.sync(alarmas); }, [alarmas]);
  useEffect(() => () => alarmSound.disableAudio(), []);
  useEffect(() => { const r = document.documentElement; noche ? r.classList.add("dark") : r.classList.remove("dark"); localStorage.setItem(NOCHE_KEY, noche ? "1" : "0"); return () => r.classList.remove("dark"); }, [noche]);
  useEffect(() => { sessionStorage.setItem(CTX_KEY, ctxDesktop ? "1" : "0"); }, [ctxDesktop]);
  // La preferencia de Zona 4 la controla SOLO la monitorista: una alarma nueva jamás la reabre.
  const toggleContexto = () => {
    if (window.matchMedia("(min-width:1280px)").matches) setCtxDesktop((v) => !v);
    else setCtxOpen((v) => !v);
  };

  const loadList = useCallback(async () => {
    const myTicket = (ticketRef.current += 1);
    try {
      const data = await getAlarmas();
      if (myTicket !== ticketRef.current) return;   // respuesta vieja: no pisa una más reciente
      setAlarmas(data); setLoadError(false); setLastOk(Date.now());
      setListaParcial(data?.incompleta ? textoListaIncompleta(data) : "");
      const nuevos = data.map((a) => a.id).filter((id) => !seenRef.current.has(id));
      if (nuevos.length) { nuevos.forEach((id) => seenRef.current.add(id)); api.post("/alarmas/vistas", { ids: nuevos }).catch(() => {}); }
    } catch (e) { if (myTicket === ticketRef.current) setLoadError(true); }
  }, []);
  const loadDetail = useCallback(async (id) => { if (!id) { setDetail(null); return; } try { const { data } = await api.get(`/alarmas/${id}`); setDetail(data); } catch { setDetail(null); } }, []);

  useEffect(() => { loadList(); const t = setInterval(() => { loadList(); if (selRef.current) loadDetail(selRef.current); }, 2000); return () => clearInterval(t); }, [loadList, loadDetail]);
  useEffect(() => { const s = (cfg.sesion_heartbeat_seg || 15) * 1000; const ping = () => api.post("/sesion/heartbeat").catch(() => {}); ping(); const t = setInterval(ping, s); return () => clearInterval(t); }, [cfg.sesion_heartbeat_seg]);

  useEffect(() => { const aid = searchParams.get("alarma"); if (aid) { setSelectedId(aid); loadDetail(aid); searchParams.delete("alarma"); setSearchParams(searchParams, { replace: true }); } /* eslint-disable-next-line */ }, []);

  const onSelect = (id) => { setSelectedId(id); loadDetail(id); setColaOpen(false); };

  const held = contarTomadas(alarmas, uid);
  const recibidas = alarmas.filter((a) => a.estado === "recibida");
  const altaSinTomar = recibidas.filter((a) => a.prioridad === "alta").length;
  const mediaSinTomar = recibidas.filter((a) => a.prioridad === "media").length;
  const coberturaCritica = alarmas.filter((a) => a.tipo === "cobertura_perdida" && a.estado === "recibida").length;
  const counts = { altaSinTomar, mediaSinTomar, enAtencion: alarmas.filter((a) => a.estado !== "cerrada" && a.estado !== "recibida").length, coberturaCritica };
  const overload = held.alta > (cfg.max_alta_tomadas || 1);
  const live = !loadError && (Date.now() - lastOk) < 7000;


  const persona = alarmas.filter((a) => !a.es_tecnica);
  const tecnicas = alarmas.filter((a) => a.es_tecnica);
  const personaOrd = ordenarCola(persona, nowMs);
  const tecnicasOrd = ordenarCola(tecnicas, nowMs);

  // Atender siguiente: SOLO alarmas de persona (las técnicas se atienden aparte, nunca compiten
  // por este turno). Orden congelado: toda ALTA de persona -> toda MEDIA de persona.
  const siguiente = ordenarCola(recibidas.filter((a) => !a.es_tecnica), nowMs)[0];
  let sigBloqueo = "";
  if (siguiente) {
    if (siguiente.prioridad === "alta" && held.alta >= (cfg.max_alta_tomadas || 1)) sigBloqueo = "Cierra o libera tu ALTA antes de tomar otra";
    else if (siguiente.prioridad === "media" && held.media >= (cfg.max_media_tomadas || 3)) sigBloqueo = `Máximo ${cfg.max_media_tomadas || 3} MEDIA en atención`;
  }

  const doTake = async (id, sobrecarga = false, select = true) => {
    setBusy(true);
    try {
      await api.post(`/alarmas/${id}/tomar`, { sobrecarga });
      toast.success("Alarma tomada");
      await loadList(); if (select) { setSelectedId(id); await loadDetail(id); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.motivo) toast.error(d.mensaje);
      else if (e?.response?.status === 409) toast.error(`Ya la tomó ${d?.operador || "otro operador"}.`);
      else toast.error(formatApiErrorDetail(d));
      await loadList();
    } finally { setBusy(false); }
  };
  const onTake = () => doTake(selectedId, false);
  const onAtenderTambien = () => doTake(selectedId, true);
  const onAtenderSiguiente = () => { if (siguiente && !sigBloqueo) doTake(siguiente.id, false); else if (sigBloqueo) toast.error(sigBloqueo); };

  const onLiberar = async () => { setBusy(true); try { await api.post(`/alarmas/${selectedId}/liberar`, {}); toast.success("Alarma liberada a la cola"); setSelectedId(null); setDetail(null); await loadList(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); } };
  const onSolicitarApoyo = async () => { setBusy(true); try { const { data } = await api.post(`/alarmas/${selectedId}/solicitar-apoyo`); const g = data.guardia || {}; toast.success(g.telefono ? `Apoyo registrado. Guardia: ${g.nombre || ""} ${g.telefono}` : "Apoyo registrado (configura el teléfono del guardia)"); await loadDetail(selectedId); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); } };

  const onTransition = async (estado) => { setBusy(true); try { await api.post(`/alarmas/${selectedId}/transicion`, { estado }); await loadDetail(selectedId); await loadList(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); } };
  const onStep = async (orden) => { setBusy(true); try { await api.post(`/alarmas/${selectedId}/pasos/${orden}/ejecutar`); await loadDetail(selectedId); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); } };
  const onVerify = async (ok, nota) => { setBusy(true); try { await api.post(`/alarmas/${selectedId}/verificacion`, { contacto_logrado: ok, nota }); toast.success(ok ? "Verificación registrada" : "Sin contacto: escalada a ALTA"); await loadDetail(selectedId); await loadList(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); } };
  const onConfirmClose = async (payload) => {
    setBusy(true);
    // TECH-001: clasificación técnica NO terminal => seguimiento; la incidencia sigue abierta.
    if (detail?.es_tecnica && payload.terminal === false) {
      try { await api.post(`/alarmas/${selectedId}/seguimiento-tecnico`, { clasificacion: payload.clasificacion, nota: payload.nota_cierre }); toast.success("Seguimiento registrado. La incidencia sigue abierta."); setCloseOpen(false); await loadDetail(selectedId); await loadList(); }
      catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
      finally { setBusy(false); }
      return;
    }
    try { await api.post(`/alarmas/${selectedId}/cerrar`, payload); toast.success(detail?.es_tecnica ? "Incidencia cerrada" : "Alarma cerrada"); setCloseOpen(false); localStorage.removeItem(`galarya_nota_${selectedId}`); setNotas((p) => { const n = { ...p }; delete n[selectedId]; return n; }); setSelectedId(null); setDetail(null); await loadList(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); } };

  const setNota = (v) => { setNotas((p) => ({ ...p, [selectedId]: v })); localStorage.setItem(`galarya_nota_${selectedId}`, v); };
  const notaActual = selectedId ? (notas[selectedId] ?? localStorage.getItem(`galarya_nota_${selectedId}`) ?? "") : "";

  const mias = held.mias;
  const takeBlockedAlta = detail && !detail.es_tecnica && detail.estado === "recibida" && detail.prioridad === "alta" && held.alta >= (cfg.max_alta_tomadas || 1);
  const incidenciaTecnica = detail && tecnicas.find((t) => t.sitio_id === detail.sitio_id && t.estado !== "cerrada")
    ? `${tipoMeta(tecnicas.find((t) => t.sitio_id === detail.sitio_id && t.estado !== "cerrada").tipo).label} en este sitio` : null;
  const tecnicasAbiertas = tecnicas.filter((t) => t.estado !== "cerrada").length;
  const fechaHora = (() => {
    const d = new Date(nowMs);
    const parts = new Intl.DateTimeFormat("es-MX", { timeZone: "America/Tijuana", weekday: "long", day: "2-digit", month: "short", year: "numeric" }).formatToParts(d);
    const get = (t) => (parts.find((p) => p.type === t) || {}).value || "";
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    const hora = new Intl.DateTimeFormat("es-MX", { timeZone: "America/Tijuana", hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
    return `${cap(get("weekday"))} ${get("day")} ${cap(get("month").replace(".", ""))} ${get("year")} · ${hora} · Tijuana`;
  })();

  const onNotaGestion = async (texto) => {
    if (!texto || !texto.trim()) return;
    setBusy(true);
    try { await api.post(`/alarmas/${selectedId}/nota`, { texto }); toast.success("Nota registrada"); await loadDetail(selectedId); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleAudio = () => { if (audioOn) { alarmSound.disableAudio(); setAudioOn(false); } else { const ok = alarmSound.enableAudio(); setAudioOn(ok); if (ok) alarmSound.sync(alarmas); } };

  const Cola = (
    <div className="p-3 space-y-3">
      {siguiente ? (
        <button data-testid="btn-atender-siguiente" onClick={onAtenderSiguiente} disabled={busy}
          className={`w-full rounded-xl px-3 py-2.5 text-sm font-semibold ${sigBloqueo ? "bg-slate-100 text-slate-500 dark:bg-slate-700 cursor-not-allowed" : "bg-[#01CBE3] text-[#012472] hover:bg-[#01B5CB]"}`}
          title={sigBloqueo}>
          {sigBloqueo || "Atender siguiente"}
        </button>
      ) : (
        <div data-testid="cola-estado-vacio" className="w-full rounded-xl border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5 text-center text-sm text-slate-500 dark:text-slate-400">
          {tecnicasOrd.length > 0 ? "Sin alarmas de persona pendientes" : "Sin alarmas pendientes"}
        </div>
      )}
      {listaParcial && <div data-testid="lista-incompleta" className="rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"><strong>Lista incompleta.</strong> {listaParcial}</div>}
      {loadError && <div data-testid="conn-error" className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">SIN SEÑAL — mostrando la última información.</div>}
      {overload && <div data-testid="overload-warning" className="rounded-lg border border-[#9B2C2C]/50 bg-[#9B2C2C]/10 px-3 py-2 text-sm font-semibold text-[#9B2C2C] dark:text-[#F2B8B8]">⚠ Estás en SOBRECARGA ({held.alta} ALTA en atención).</div>}
      {personaOrd.length > 0 && tecnicasOrd.length > 0 && <p data-testid="persona-divider" className="px-1 text-xs font-semibold uppercase text-slate-400">Alarmas de persona ({personaOrd.length})</p>}
      {personaOrd.map((a) => <AlarmRow key={a.id} alarma={a} selected={a.id === selectedId} onSelect={onSelect} nowMs={nowMs} />)}
      {tecnicasOrd.length > 0 && (
        <div data-testid="tecnicas-section" className="pt-2 mt-1 border-t border-dashed border-slate-200 dark:border-slate-700 space-y-3">
          <p data-testid="tecnicas-divider" className="px-1 pt-1 text-xs font-semibold uppercase text-slate-400 flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> Incidencias técnicas ({tecnicasOrd.length})</p>
          {tecnicasOrd.map((a) => <AlarmRow key={a.id} alarma={a} selected={a.id === selectedId} onSelect={onSelect} nowMs={nowMs} />)}
        </div>
      )}
      {alarmas.length === 0 && !loadError && <p className="text-center text-sm text-slate-400 py-10">No hay alarmas activas.</p>}
    </div>
  );

  return (
    <div data-testid="consola-page" className="h-full flex flex-col bg-[#F8FAFC] dark:bg-[#0F172A]">
      <TopBar counts={counts} live={live} fechaHora={fechaHora} audioOn={audioOn} onToggleAudio={toggleAudio}
        onTestSound={() => alarmSound.test()} coberturaCritica={coberturaCritica}
        onVerCobertura={() => { const t = tecnicas.find((x) => x.tipo === "cobertura_perdida" && x.estado === "recibida"); if (t) onSelect(t.id); }}
        onToggleCola={() => setColaOpen((v) => !v)} onToggleContexto={toggleContexto} ctxAbierto={ctxDesktop} noche={noche} onToggleNoche={() => setNoche((v) => !v)} />

      {/* Barra de nueva ALTA cuando la cola está oculta (<lg) */}
      {altaSinTomar > 0 && (
        <div data-testid="barra-nueva-alta" className="lg:hidden flex items-center justify-between gap-2 bg-red-600 text-white px-4 py-1.5 text-sm">
          <span className="inline-flex items-center gap-2"><ShieldOff className="h-4 w-4" /> {altaSinTomar} ALTA sin tomar</span>
          <button onClick={() => setColaOpen(true)} className="underline font-semibold">Ver</button>
        </div>
      )}

      <div className="flex-1 min-h-0 flex relative">
        {/* Zona 2 — Cola */}
        <aside className={`${colaOpen ? "flex absolute z-30 inset-y-0 left-0 w-[85%] max-w-[360px] shadow-xl" : "hidden"} lg:flex lg:relative lg:w-[340px] lg:shadow-none shrink-0 flex-col overflow-y-auto border-r border-slate-200 dark:border-slate-700 bg-[#F8FAFC] dark:bg-[#0F172A]`}>
          {Cola}
        </aside>
        {colaOpen && <div className="lg:hidden absolute inset-0 z-20 bg-black/30" onClick={() => setColaOpen(false)} />}

        {/* Zona 3 — Área de trabajo */}
        <section className="flex-1 flex flex-col min-w-0 overflow-y-auto">
          {/* Mis casos tabs */}
          {mias.length > 0 && (
            <div data-testid="mis-casos" className="shrink-0 flex items-center gap-2 overflow-x-auto border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2">
              <span className="text-xs font-semibold text-slate-400 shrink-0">Mis casos:</span>
              {mias.map((a) => { const pm = prioridadMeta(a.prioridad); return (
                <button key={a.id} data-testid={`tab-caso-${a.id}`} onClick={() => onSelect(a.id)}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border ${a.id === selectedId ? "border-[#01CBE3] bg-[#01CBE3]/10 text-[#012472] dark:text-[#80B3FF]" : "border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300"}`}>
                  {a.es_tecnica
                    ? <><Wrench className="h-3 w-3 text-[#9B2C2C] dark:text-[#F2B8B8]" /> {tipoMeta(a.tipo).label} · {a.sitio_nombre}</>
                    : <><span className={pm.text}>●</span> {a.persona_nombre || "Persona"} · {tipoMeta(a.tipo).label}</>}
                </button>); })}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {!selectedId ? (
              <ResumenOperativo alarmas={alarmas} mias={mias} counts={counts} nowMs={nowMs} tecnicasAbiertas={tecnicasAbiertas} />
            ) : !detail ? (
              <p className="p-6 text-slate-400">Cargando detalle…</p>
            ) : (
              <div className="p-4 md:p-6">
                <button data-testid="btn-volver-cola" onClick={() => { setSelectedId(null); setDetail(null); }} className="lg:hidden mb-3 inline-flex items-center gap-1.5 text-sm text-slate-500"><ArrowLeft className="h-4 w-4" /> Volver</button>
                <AlarmDetail detail={detail} nowMs={nowMs} busy={busy} onTake={onTake} onTransition={onTransition}
                  onStep={onStep} onVerify={onVerify} onCloseClick={() => setCloseOpen(true)}
                  onLiberar={onLiberar} onSolicitarApoyo={onSolicitarApoyo} onAtenderTambien={onAtenderTambien}
                  takeBlockedAlta={takeBlockedAlta} incidenciaTecnica={incidenciaTecnica}
                  notaDraft={notaActual} onNotaDraft={setNota} onNotaGestion={onNotaGestion} />
              </div>
            )}
          </div>
        </section>

        {/* Zona 4 — Contexto */}
        <aside className={`${ctxOpen ? "flex absolute z-30 inset-y-0 right-0 w-[85%] max-w-[320px] shadow-xl" : "hidden"} ${ctxDesktop ? "xl:flex xl:relative xl:w-[300px] xl:shadow-none" : "xl:hidden"} shrink-0 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800`}>
          <ContextPanel alarma={detail} tecnicasCount={tecnicas.filter((t) => t.estado !== "cerrada").length} />
        </aside>
        {ctxOpen && <div className="xl:hidden absolute inset-0 z-20 bg-black/30" onClick={() => setCtxOpen(false)} />}
      </div>

      {/* GOV-004: en un caso de PERSONA el cierre humano exige nota factual (el backend la valida). */}
      <CloseAlarmModal open={closeOpen} onOpenChange={setCloseOpen} onConfirm={onConfirmClose} submitting={busy} initialNota={notaActual}
        notaObligatoria={!!detail && !detail.es_tecnica}
        tecnicoMode={!!detail && !!detail.es_tecnica}
        resolucionesTecnicas={detail?.resoluciones_tecnicas || null} />
    </div>
  );
}
